export function SellMyApartmentsForCash() {
  return (
    <section className="py-20 bg-gray-50" id="sell-for-cash">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell My Apartments for Cash - Get Immediate Liquidity
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cash sales eliminate financing contingencies, appraisal issues, and lengthy closing processes. 
            Learn why cash offers are often the best choice for apartment building sellers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/692e389974ebff6f8b34014c1e7d36fd.jpg"
              alt="Cash payment for apartment building sale"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Cash Sales Are Superior</h3>
            <p className="text-gray-600 mb-8">
              Cash transactions eliminate the most common reasons deals fall through, providing certainty 
              and speed that traditional financed purchases simply cannot match.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Financing Contingencies</h4>
                  <p className="text-gray-600">
                    Eliminate the risk of deals falling through due to buyer financing issues, 
                    bank delays, or changing lending requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Skip the Appraisal Process</h4>
                  <p className="text-gray-600">
                    No need to worry about low appraisals that could reduce the sale price 
                    or kill the deal entirely.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Faster Closing Timeline</h4>
                  <p className="text-gray-600">
                    Close in 7-30 days instead of 45-90 days typical with traditional financing, 
                    getting you paid sooner.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Stronger Negotiating Position</h4>
                  <p className="text-gray-600">
                    Cash offers are more attractive to sellers and often win in competitive 
                    situations even at slightly lower prices.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Cash vs. Financed Offers Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="px-6 py-4 text-left text-lg font-semibold text-gray-900">Factor</th>
                  <th className="px-6 py-4 text-center text-lg font-semibold text-green-700">Cash Offer</th>
                  <th className="px-6 py-4 text-center text-lg font-semibold text-red-700">Financed Offer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 font-medium text-gray-900">Closing Timeline</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">7-30 days</td>
                  <td className="px-6 py-4 text-center text-red-600">45-90 days</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Deal Certainty</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">95%+ success rate</td>
                  <td className="px-6 py-4 text-center text-red-600">70-80% success rate</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-gray-900">Appraisal Required</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">No</td>
                  <td className="px-6 py-4 text-center text-red-600">Yes</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Financing Contingency</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">None</td>
                  <td className="px-6 py-4 text-center text-red-600">30-45 days</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-gray-900">Buyer Qualification Risk</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Minimal</td>
                  <td className="px-6 py-4 text-center text-red-600">Significant</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Repairs/Credits Requested</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Rare</td>
                  <td className="px-6 py-4 text-center text-red-600">Common</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-bank-line text-3xl text-blue-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Proof of Funds Required</h4>
            <p className="text-gray-600 mb-4">
              Legitimate cash buyers provide bank statements or proof of funds letters showing 
              they have the money available.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">Always verify before accepting</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-3xl text-green-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Due Diligence Period</h4>
            <p className="text-gray-600 mb-4">
              Cash buyers typically request 10-15 days for inspections and document review, 
              much shorter than financed purchases.
            </p>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800 font-medium">Faster decision timeline</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-contract-line text-3xl text-purple-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Simplified Contracts</h4>
            <p className="text-gray-600 mb-4">
              No financing contingencies, appraisal clauses, or loan-related addendums 
              make contracts cleaner and simpler.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-800 font-medium">Fewer contingencies to fail</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-12 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            <i className="ri-lightbulb-line text-yellow-600 mr-3"></i>
            How to Evaluate Cash Offers
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Red Flags to Watch For:</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">No proof of funds provided</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Unusually long inspection periods</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Requests for seller financing</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Excessive contingencies in contract</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Good Signs to Look For:</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Bank statements or proof of funds</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Track record of closed deals</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Professional references available</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700">Clear, straightforward terms</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Get Your Guaranteed Cash Offer Today
          </h3>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            We are experienced cash buyers with a proven track record. Get a fair, no-obligation cash offer 
            for your apartment building with proof of funds and a guaranteed closing timeline.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">$50M+</div>
              <div className="text-lg">Available Cash</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">300+</div>
              <div className="text-lg">Properties Purchased</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">15 Days</div>
              <div className="text-lg">Average Closing</div>
            </div>
          </div>
          <button className="bg-white text-green-600 px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
            Request Cash Offer Now
          </button>
        </div>
      </div>
    </section>
  );
}